import React from "react";
import styled from "styled-components";

const AboutWrapper = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  background: linear-gradient(
    135deg,
    var(--primary-color) 0%,
    var(--background-dark) 100%
  );
  color: white;
  padding: 120px 0 80px;
  text-align: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 20px;
  color: white;
  font-family: "Inter", sans-serif;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: #e8e8e8;
  max-width: 600px;
  margin: 0 auto;
`;

const ContentSection = styled.section`
  padding: 80px 0;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  margin-bottom: 80px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ContentText = styled.div`
  h2 {
    font-size: 2.5rem;
    color: var(--text-dark);
    margin-bottom: 30px;

    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }

  p {
    font-size: 1.1rem;
    line-height: 1.7;
    color: var(--text-light);
    margin-bottom: 20px;

    strong {
      color: var(--text-dark);
    }
  }
`;

const ContentImage = styled.div`
  img {
    width: 100%;
    height: 400px;
    object-fit: cover;
    border-radius: 15px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
`;

const StatsSection = styled.section`
  background: var(--background-light);
  padding: 80px 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
`;

const StatCard = styled.div`
  background: white;
  padding: 40px 30px;
  border-radius: 15px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }

  .icon {
    width: 60px;
    height: 60px;
    background: var(--secondary-color);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    color: white;
    font-size: 1.5rem;
  }

  h3 {
    font-size: 1.5rem;
    color: var(--text-dark);
    margin-bottom: 15px;
  }

  p {
    color: var(--text-light);
    line-height: 1.6;
  }
`;

const About = () => {
  return (
    <AboutWrapper>
      <HeroSection>
        <Container>
          <HeroTitle>About Baypoint Storage</HeroTitle>
          <HeroSubtitle>
            Your trusted partner for premium shipping containers and storage
            solutions across the United States
          </HeroSubtitle>
        </Container>
      </HeroSection>

      <ContentSection>
        <Container>
          <ContentGrid>
            <ContentText>
              <h2>Our Story</h2>
              <p>
                <strong>Baypoint Storage</strong> was founded with a simple
                mission: to make high-quality shipping containers accessible to
                everyone. Whether you're a homeowner looking for extra storage
                space, a business in need of temporary facilities, or a
                developer working on innovative container projects, we're here
                to help.
              </p>
              <p>
                Based in <strong>Colorado</strong> with strategic partnerships
                across <strong>Oklahoma, Wyoming and Utah</strong>, we've built
                a nationwide network that ensures fast, reliable delivery to all
                50 states. Our team combines decades of industry experience with
                a commitment to customer satisfaction that sets us apart.
              </p>
            </ContentText>
            <ContentImage>
              <img src="/assets/2025/08/Image2.jpg" alt="Our facility" />
            </ContentImage>
          </ContentGrid>

          <ContentGrid style={{ flexDirection: "row-reverse" }}>
            <ContentText>
              <h2>Why Choose Us?</h2>
              <p>
                We understand that buying a container is more than just a
                purchase—it's an investment in your future. That's why we go
                beyond simply selling containers to provide complete solutions
                tailored to your specific needs.
              </p>
              <p>
                From <strong>brand-new one-trip containers</strong> to carefully
                inspected
                <strong> used units</strong>, every container in our inventory
                meets our strict quality standards. We also offer custom
                modifications, turning your container into exactly what you
                envision.
              </p>
            </ContentText>
            <ContentImage>
              <img
                src="/assets/2025/08/Image3.jpg"
                alt="Container modifications"
              />
            </ContentImage>
          </ContentGrid>
        </Container>
      </ContentSection>

      <StatsSection>
        <Container>
          <h2 className="text-center mb-5">Our Impact</h2>
          <StatsGrid>
            <StatCard>
              <div className="icon">
                <i className="fas fa-users"></i>
              </div>
              <h3>2,000+</h3>
              <p>Happy customers served across the United States</p>
            </StatCard>

            <StatCard>
              <div className="icon">
                <i className="fas fa-map-marker-alt"></i>
              </div>
              <h3>50 States</h3>
              <p>Nationwide delivery coverage with reliable logistics</p>
            </StatCard>

            <StatCard>
              <div className="icon">
                <i className="fas fa-shipping-fast"></i>
              </div>
              <h3>5,000+</h3>
              <p>Containers delivered safely and on time</p>
            </StatCard>

            <StatCard>
              <div className="icon">
                <i className="fas fa-star"></i>
              </div>
              <h3>4.8/5</h3>
              <p>Average customer satisfaction rating</p>
            </StatCard>
          </StatsGrid>
        </Container>
      </StatsSection>
    </AboutWrapper>
  );
};

export default About;
